import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { TemplateCategoriesService, TemplateCategory, TemplateCategoryFormData } from '../template-categories.service';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';
import { Generic } from 'src/app/interfaces/generic.interface';
import { CommonService } from 'src/app/services/common.service';
import { NotificationService } from 'src/app/services/notification.service';

@Component({
    selector: 'app-add-template-category',
    templateUrl: './add-template-category.component.html',
    styleUrls: ['./add-template-category.component.scss']
})
export class AddTemplateCategoryComponent implements OnInit, OnDestroy {
    // general error
    errorTitle: string = environment.config.customNotifications.headers.error;
    errorIcon: string = environment.config.customNotifications.icons.error;
    errorType: string = environment.config.customNotifications.icons.error;
    helper = helper;

    token!: string;
    isLoading = false;

    form: FormGroup = new FormGroup({
        name: new FormControl('', [Validators.required]),
        description: new FormControl('', []),
        directie_id: new FormControl(-1, [Validators.required]),
        compartiment_id: new FormControl(-1, [Validators.required]),
        parent_category_id: new FormControl(-1),
        order_index: new FormControl(0),
    });

    // Parent category selection
    availableParentCategories: TemplateCategory[] = [];

    directiiActive: Generic[] = this.data;
    compartimenteActive!: Generic[];

    private addDestroy$ = new Subject<void>();

    filteredDirectii: any[] = this.data;
    directiiControl = new FormControl();

    filteredCompartimente: any[] = [];
    compartimenteControl = new FormControl();

    filteredParentCategories: any[] = [];
    parentCategoriesControl = new FormControl();

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<AddTemplateCategoryComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private templateCategoriesService: TemplateCategoriesService,
        private commonService: CommonService,
        private notificationService: NotificationService,
    ) { }

    ngOnInit(): void {
        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.addDestroy$.next();
        this.addDestroy$.complete();
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directiiActive;
                    return of(this.directiiActive);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimenteActive;
                    return of(this.compartimenteActive);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.parentCategoriesControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredParentCategories = this.availableParentCategories;
                    return of(this.availableParentCategories);
                }
                return this.filterParentCategories(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredParentCategories = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiActive.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onDirectieSelectClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(directie =>
                directie.id === this.form.get('directie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directiiActive;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.form.controls['compartiment_id'].patchValue(-1);
        this.form.controls['parent_category_id'].patchValue(-1);

        if (directieId == -1) {
            this.compartimenteActive = [];
            this.filteredCompartimente = [];
            this.availableParentCategories = [];
            this.filteredParentCategories = [];
            return;
        }

        if (directieId) {
            this.loadCompartimenteActive(directieId);
            this.loadAvailableParentCategories();
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;
        let compartimenteObservable$ = directieId
            ? this.commonService.getCompartimenteByDirectieId(directieId)
            : this.commonService.getCompartimenteActive();

        compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => (this.isLoading = false))
        ).subscribe({
            next: (res) => {
                this.compartimenteActive = [...res];
                this.filteredCompartimente = [...res];
            },
            error: (err) => {
                console.log(err);
                this.filteredCompartimente = [];
            },
        })
    }

    filterCompartimente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.compartimenteActive.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onCompartimentSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.form.get('compartiment_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimenteActive;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onCompartimentChange(compartimentId: number) {
        this.form.controls['parent_category_id'].patchValue(-1);

        if (compartimentId) {
            this.loadAvailableParentCategories();
        }
    }

    loadAvailableParentCategories(): void {
        const directieId = this.form.get('directie_id')?.value;
        const compartimentId = this.form.get('compartiment_id')?.value !== -1 ? this.form.get('compartiment_id')?.value : null;

        this.templateCategoriesService.getCategoriesByOrganization(directieId, compartimentId)
            .subscribe({
                next: (response) => {
                    if (response.errors === false) {
                        this.availableParentCategories = response.data || [];
                        this.filteredParentCategories = response.data;
                    }
                },
                error: (error) => {
                    console.error('Error loading parent categories:', error);
                    this.availableParentCategories = [];
                }
            });
    }

    filterParentCategories(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.availableParentCategories.filter(category =>
            this.normalizeString(category.name).includes(normalizedSearchTerm)
        ));
    }

    onParentCategoriesSelectClosed() {
        const selectedValue = this.parentCategoriesControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredParentCategories.some(category =>
                category.id === this.form.get('parent_category_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.parentCategoriesControl.setValue(null);
                this.filteredParentCategories = this.availableParentCategories;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    displayParentCategory(category: TemplateCategory): string {
        return category ? category.name : '';
    }

    onParentCategorySelected(category: TemplateCategory): void {
        this.form.patchValue({
            parent_category_id: category.id
        });
    }

    submitForm() {
        this.isLoading = true;

        const {
            name,
            description,
            directie_id,
            compartiment_id,
            parent_category_id,
            order_index
        } = this.form.value as TemplateCategoryFormData;

        const newRequestType: TemplateCategoryFormData = {
            name,
            description,
            directie_id,
            compartiment_id,
            parent_category_id,
            order_index
        };

        this.templateCategoriesService
            .store(newRequestType)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0]
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error
                    }
                    this.notificationService.warningSwal(
                        helper.dialogConfig.headers.errorAtentie,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }
}